import { cn } from '@nexo/ui';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { Logo } from '../../components/Logo';
import { formatLongDate } from '../../lib/format';
import { legalDocuments, type LegalDocumentId } from './documents';
import { legalOwner } from './legalOwner';

const tabs: { id: LegalDocumentId; path: string }[] = [
  { id: 'privacy', path: '/privacy' },
  { id: 'terms', path: '/terms' },
  { id: 'notice', path: '/legal' },
];

/** Privacy policy, terms of use and legal notice (one page per document, in the UI language). */
export function LegalPage({ document }: { document: LegalDocumentId }) {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en' : 'es';
  const docs = legalDocuments[lang];
  const doc = docs[document];

  return (
    <div className="flex min-h-full flex-col bg-bg">
      <header className="mx-auto flex w-full max-w-3xl items-center justify-between px-5 py-5">
        <Link to="/">
          <Logo />
        </Link>
        <LanguageSwitcher compact className="text-muted hover:text-text" />
      </header>
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 pt-4 pb-20">
        <nav className="mb-8 flex flex-wrap gap-1 border-b border-border">
          {tabs.map((tab) => (
            <NavLink
              key={tab.id}
              to={tab.path}
              className={({ isActive }) =>
                cn(
                  '-mb-px border-b-2 px-3 py-2 text-sm',
                  isActive
                    ? 'border-accent font-medium text-text'
                    : 'border-transparent text-muted hover:text-text',
                )
              }
            >
              {docs[tab.id].title}
            </NavLink>
          ))}
        </nav>
        <h1 className="text-3xl font-semibold tracking-tight">{doc.title}</h1>
        <p className="mt-2 text-sm text-muted">
          {docs.updated} {formatLongDate(legalOwner.updatedAt)}
        </p>
        <div className="mt-8 flex flex-col gap-7">
          {doc.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-lg font-semibold">{section.heading}</h2>
              {section.body.map((paragraph) =>
                Array.isArray(paragraph) ? (
                  <ul
                    key={paragraph.join()}
                    className="mt-2 list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-muted"
                  >
                    {paragraph.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={paragraph} className="mt-2 text-[15px] leading-relaxed text-muted">
                    {paragraph}
                  </p>
                ),
              )}
            </section>
          ))}
        </div>
      </main>
    </div>
  );
}
